import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { PayMethodPicker, payTotal } from '../components/PayMethodPicker'
import { ReviewPager, ReviewTimeline, StarsRow } from '../components/ReviewTimeline'
import {
  api,
  formatVnd,
  type CategoryDetail,
  type PayMethod,
  type Review,
  type ReviewStats,
} from '../lib/api'

const KEY_ID = 14
const HERO_IMG = '/image/minecraft-key-product.jpg'
const PAY_IMG =
  'https://accminecraft.net/image/homepage2/cac-phuong-thuc-thanh-toan-khi-mua-minecraft.png'

const PLATFORMS = [
  { icon: 'fa-brands fa-windows', label: 'Windows', title: 'Windows' },
  { icon: 'fa-brands fa-apple', label: 'macOS', title: 'macOS' },
  { icon: 'fa-brands fa-linux', label: 'Linux', title: 'Linux' },
  { icon: 'fa-solid fa-gamepad', label: 'Console & Mobile', title: 'Console & Mobile (Bedrock)' },
]

const TRUST = [
  { icon: 'fa-solid fa-bolt', label: 'Giao tức thì' },
  { icon: 'fa-solid fa-shield-halved', label: 'Chính hãng 100%' },
  { icon: 'fa-solid fa-globe', label: 'Global' },
  { icon: 'fa-solid fa-headset', label: 'Hỗ trợ 24/7' },
]

const PILLARS = [
  { icon: 'fa-solid fa-cube', title: 'Xây dựng', desc: 'Thế giới vô hạn' },
  { icon: 'fa-solid fa-users', title: 'Chơi cùng', desc: 'Bạn bè & server' },
  { icon: 'fa-solid fa-gears', title: 'Mods', desc: 'Add-ons & modpack' },
  { icon: 'fa-solid fa-mobile-screen-button', title: 'Đa nền tảng', desc: 'PC · Console · Mobile' },
]

const FEATURES = [
  { icon: 'fa-solid fa-key', text: 'Một key — Java & Bedrock trên cùng tài khoản Microsoft' },
  { icon: 'fa-solid fa-globe', text: 'Global CD KEY — không cần VPN khi nhập' },
  { icon: 'fa-brands fa-microsoft', text: 'Redeem tại Microsoft Store / redeem.microsoft.com' },
  { icon: 'fa-solid fa-infinity', text: 'Bản quyền vĩnh viễn, chơi không giới hạn máy' },
  { icon: 'fa-solid fa-envelope-open-text', text: 'Giao key tự động qua email + trên website' },
  { icon: 'fa-solid fa-file-invoice', text: 'Có hóa đơn mua từ Microsoft' },
  { icon: 'fa-solid fa-desktop', text: 'Hỗ trợ UltraViewer cài đặt nếu cần' },
]

type TabId = 'desc' | 'reviews' | 'guide'

export function BuyKeyPage() {
  const { user, refresh } = useAuth()
  const nav = useNavigate()
  const [cat, setCat] = useState<CategoryDetail | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [qty, setQty] = useState(1)
  const [payMethod, setPayMethod] = useState<PayMethod>('bank')
  const [tab, setTab] = useState<TabId>('desc')

  const [reviews, setReviews] = useState<Review[]>([])
  const [stats, setStats] = useState<ReviewStats | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [revLoading, setRevLoading] = useState(false)

  useEffect(() => {
    api
      .category(String(KEY_ID))
      .then((r) => setCat(r.category))
      .catch((e) => setErr(e.message))
  }, [])

  useEffect(() => {
    if (tab !== 'reviews') return
    setRevLoading(true)
    api
      .reviews({ category_id: KEY_ID, page, per_page: 8 })
      .then((r) => {
        setReviews(r.items)
        setStats(r.stats)
        setTotalPages(r.total_pages || 1)
      })
      .catch(() => {
        setReviews([])
      })
      .finally(() => setRevLoading(false))
  }, [tab, page])

  async function onOrder(e: FormEvent) {
    e.preventDefault()
    if (!user) {
      nav('/login', { state: { from: '/mua-minecraft' } })
      return
    }
    if (!cat) return
    setBusy(true)
    setErr('')
    try {
      const res = await api.createKeyOrder({
        category_id: cat.id,
        quantity: qty,
        pay_method: payMethod,
      })
      if (payMethod === 'bank') {
        nav(`/checkout/${res.order.id}`)
      } else {
        await refresh()
        nav(`/account/don/${res.order.id}`)
      }
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi đặt hàng')
    } finally {
      setBusy(false)
    }
  }

  if (err && !cat) return <p className="note">{err}</p>
  if (!cat) return <p className="muted">Đang tải…</p>

  const price = cat.price_transfer || 640000
  const listPrice = cat.price || 800000
  const discount =
    listPrice > price ? Math.round(((listPrice - price) / listPrice) * 100) : 0

  return (
    <div className="buykey">
      <section className="buykey-hero">
        <div className="buykey-hero-media">
          <img src={HERO_IMG} alt="Minecraft Java & Bedrock Edition" />
          <div className="buykey-edition-badges" aria-hidden>
            <span>
              <i className="fa-brands fa-java" /> Java
            </span>
            <span>
              <i className="fa-solid fa-cube" /> Bedrock
            </span>
          </div>
        </div>
        <div className="buykey-hero-info">
          <p className="buykey-eyebrow">
            <i className="fa-solid fa-certificate" aria-hidden /> CD KEY chính hãng Microsoft
          </p>
          <h1>{cat.name}</h1>
          <p className="buykey-submeta">
            <span>
              <i className="fa-solid fa-check" aria-hidden /> Official
            </span>
            <span>
              <i className="fa-solid fa-globe" aria-hidden /> Global
            </span>
            <span>
              <i className="fa-solid fa-laptop" aria-hidden /> PC
            </span>
          </p>

          <div className="buykey-platforms" aria-label="Nền tảng hỗ trợ">
            {PLATFORMS.map((p) => (
              <span key={p.label} className="buykey-platform" title={p.title}>
                <i className={p.icon} aria-hidden />
                <span>{p.label}</span>
              </span>
            ))}
          </div>

          <p className="buykey-price">
            <span className="buykey-price-now">{formatVnd(price)}</span>
            <s className="buykey-price-old">{formatVnd(listPrice)}</s>
            {discount > 0 ? (
              <span className="buykey-discount">-{discount}%</span>
            ) : null}
          </p>

          <div className="buykey-trust" aria-label="Cam kết">
            {TRUST.map((t) => (
              <span key={t.label} className="buykey-trust-item">
                <i className={t.icon} aria-hidden />
                <span>{t.label}</span>
              </span>
            ))}
          </div>

          <ul className="buykey-features">
            {FEATURES.map((f) => (
              <li key={f.text}>
                <i className={f.icon} aria-hidden />
                <span>{f.text}</span>
              </li>
            ))}
          </ul>

          <div className="buykey-pillars" aria-label="Tính năng game">
            {PILLARS.map((p) => (
              <div key={p.title} className="buykey-pillar">
                <i className={p.icon} aria-hidden />
                <strong>{p.title}</strong>
                <span>{p.desc}</span>
              </div>
            ))}
          </div>

          <form className="buykey-order form-stack" onSubmit={onOrder}>
            <label>
              Số lượng
              <input
                type="number"
                min={1}
                max={20}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 1)}
              />
            </label>
            <PayMethodPicker
              value={payMethod}
              onChange={setPayMethod}
              priceTransfer={price}
              priceXu={listPrice}
              qty={qty}
            />
            {err ? <p className="note">{err}</p> : null}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              <i className="fa-solid fa-cart-shopping" aria-hidden />{' '}
              {busy ? 'Đang tạo…' : `Mua ngay — ${formatVnd(payTotal(payMethod, price, listPrice, qty))}`}
            </button>
            <p className="buykey-instant">
              <i className="fa-solid fa-circle-check" aria-hidden /> Giao key ngay trên giao diện
              sau thanh toán
            </p>
            <p className="muted buykey-order-hint">
              Chưa có tài khoản? <Link to="/register">Đăng ký</Link> rồi thanh toán chuyển
              khoản tự động.
            </p>
          </form>
        </div>
      </section>

      <section className="buykey-tabs panel">
        <div className="buykey-tablist" role="tablist" aria-label="Chi tiết sản phẩm">
          {(
            [
              { id: 'desc', label: 'Mô tả sản phẩm', icon: 'fa-align-left' },
              { id: 'reviews', label: 'Đánh giá', icon: 'fa-star' },
              { id: 'guide', label: 'Hướng dẫn mua', icon: 'fa-circle-question' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`buykey-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => {
                setTab(t.id)
                if (t.id === 'reviews') setPage(1)
              }}
            >
              <i className={`fa-solid ${t.icon}`} aria-hidden /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'desc' ? (
          <div className="buykey-pane prose" role="tabpanel">
            {user?.role === 'admin' ? (
              <p className="admin-edit-link">
                <Link to="/admin/key-intro">
                  <i className="fa-solid fa-pen-to-square" aria-hidden /> Sửa bài giới thiệu
                </Link>
              </p>
            ) : null}
            {cat.description || cat.short_desc ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: cat.description || cat.short_desc || '',
                }}
              />
            ) : (
              <p className="muted">Chưa có mô tả chi tiết.</p>
            )}
          </div>
        ) : null}

        {tab === 'reviews' ? (
          <div className="buykey-pane" role="tabpanel">
            {stats ? (
              <p className="buykey-rev-summary">
                <StarsRow n={Math.round(stats.avg)} />
                <strong>{stats.avg.toFixed(1).replace('.', ',')}/5</strong>
                <span className="muted">
                  · {stats.count.toLocaleString('vi-VN')} đánh giá key này
                </span>
              </p>
            ) : null}

            {revLoading ? <p className="muted">Đang tải đánh giá…</p> : null}

            <ReviewTimeline
              items={reviews}
              onReplied={(id, reply) =>
                setReviews((list) => list.map((x) => (x.id === id ? { ...x, admin_reply: reply } : x)))
              }
            />

            {!revLoading && !reviews.length ? (
              <p className="muted">Chưa có đánh giá cho sản phẩm này.</p>
            ) : null}

            <ReviewPager
              page={page}
              totalPages={totalPages}
              disabled={revLoading}
              onPage={setPage}
            />
          </div>
        ) : null}

        {tab === 'guide' ? (
          <div className="buykey-pane" role="tabpanel">
            <h2 className="buykey-guide-title">Cách mua hàng</h2>
            <div className="buykey-guide-grid">
              <img src={PAY_IMG} alt="Phương thức thanh toán" />
              <div className="buykey-ways">
                <article className="buykey-way">
                  <h3>
                    <i className="fa-solid fa-sim-card" aria-hidden /> Nạp thẻ cào
                  </h3>
                  <p>
                    Nạp thẻ vào shop — không tính phí đổi thẻ. Ví dụ nạp 10.000đ nhận 10.000 xu
                    để mua sản phẩm. Chấp nhận Viettel, Mobifone, Vinaphone, Vietnamobile,
                    Garena, Zing…
                  </p>
                </article>
                <article className="buykey-way">
                  <h3>
                    <i className="fa-solid fa-building-columns" aria-hidden /> Nạp chuyển khoản
                  </h3>
                  <p>
                    Chuyển khoản / MoMo nạp số dư trên tài khoản shop, rồi dùng số dư mua key
                    bất kỳ lúc nào.
                  </p>
                </article>
                <article className="buykey-way buykey-way-hot">
                  <h3>
                    <i className="fa-solid fa-bolt" aria-hidden /> Chuyển khoản trực tiếp
                  </h3>
                  <p>
                    Bấm <strong>Mua ngay</strong> trên trang này → tạo đơn → chuyển đúng số tiền
                    và nội dung. Key giao tự động trong khoảng 1 phút qua email và trên
                    website.
                  </p>
                </article>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}
